import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import { PageHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import { Table, TableContent, Th } from '@/components/Table';
import { PublicHolidayListResource } from '@/types/resources';
import { api } from '@/utils/api';

import PublicHolidayListFormModal from './components/PublicHolidayListFormModal';
import PublicHolidayListsTableRow from './components/PublicHolidayListsTableRow';

const StyledTable = styled(Table)`
  td,
  th {
    vertical-align: middle;
    font-weight: 700;
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      width: 64px;
      padding-right: 24px;
    }
  }
`;

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const PublicHolidays = () => {
  const [showModal, setShowModal] = useState(false);

  const {
    data: lists,
    isError,
    isLoading,
  } = useQuery(
    ['public-holiday-lists'],
    () =>
      api.get<never, PublicHolidayListResource[]>(
        '/v1/payments/freelancers/holidays/public-holiday-lists',
      ),
    {
      staleTime: 90 * 1000,
    },
  );

  return (
    <>
      <HeadingRow>
        <PageHeading>Public Holidays</PageHeading>
        <Button onClick={() => setShowModal(true)}>Add New</Button>
      </HeadingRow>
      <ScrollableTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Holidays</Th>
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!lists?.length}
              colSpan={3}
            >
              {lists?.map((list) => (
                <PublicHolidayListsTableRow key={list.id} list={list} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <AnimatePresence>
        {showModal && (
          <PublicHolidayListFormModal
            onClose={() => {
              setShowModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default PublicHolidays;
